import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { Link, useStaticQuery, graphql } from 'gatsby';
import styled from '@emotion/styled';
import { BsTag } from 'react-icons/bs';
import {
  Box,
  Flex,
  Heading,
  Text,
  Link as ExternalLink,
} from '@theme-ui/components';

const CardLink = ({ url, children }) => { 
  const isExternalLink = url.startsWith('http');
  return (
    <>
      {isExternalLink ? (
        <ExternalLink href={url} target="_blank">
          {children}
        </ExternalLink>
      ) : (
        <Link to={url}>{children}</Link>
      )}
    </>
  );
};

const CardImage = ({
  cardImage,
  imageUrl,
  defaultImg,
  externalImgAlt,
  cardTitle,
}) => (
  <StyledImgWrapper>
    {cardImage ? (
      <GatsbyImage
        image={cardImage}
        imgStyle={{ objectFit: 'cover', height: '172px', width: '100%' }}
        alt={cardImage.alt ? cardImage.alt : cardTitle}
      />
    ) : imageUrl ? (
      <StyledImg
        src={imageUrl}
        alt={externalImgAlt ? externalImgAlt : cardTitle}
      />
    ) : (
      <GatsbyImage
        image={defaultImg}
        imgStyle={{ objectFit: 'cover', height: '172px' }}
        alt={cardTitle}
      />
    )}
  </StyledImgWrapper>
);

const CardContent = ({ title, description, author, publishDate }) => {
  const authorName = author
    ? author.lastName
      ? `${author.firstName} ${author.lastName}`
      : author.firstName
    : null;
  return (
    <Box p={2}>
      <Heading as="h3" sx={titleStyle} variant="wikiHeading4">
        {title}
      </Heading>
      {authorName && publishDate ? (
        <Flex
          sx={{
            color: 'gray.6',
            justifyContent: 'space-between',
            py: 2,
            flexWrap: 'wrap',
            fontSize: '14px',
          }}>
          <Text>
            By <b>{authorName}</b>
          </Text>
          <Text>{publishDate}</Text>
        </Flex>
      ) : null}
      {description && <StyledDescription>{description}</StyledDescription>}
    </Box>
  );
};

const Tag = ({ topics }) => (
  <Text
    sx={{
      justifySelf: 'flex-end',
      p: 2,
      fontSize: '0.8rem',
      textTransform: 'uppercase',
    }}>
    <BsTag size={12} color={'#00A1F2'} style={{ marginRight: 1 }} />{' '}
    <Text>
      {topics.map(({ name, slug }, i) => (
        <Text key={`rtopic-blog-tag-${i}`}>
          {i > 0 ? ', ' : ''}
          {name}
        </Text>
      ))}
    </Text>
  </Text>
);

const ResourceCard = ({
  title,
  url,
  description,
  cardImage,
  cardImageUrl,
  externalImgAlt,
  topics,
  author,
  publishDate,
  width,
  margin,
  mb,
}) => {
  const { defaultImg } = useStaticQuery(
    graphql`
      query {
        defaultImg: file(relativePath: { eq: "blog-img.png" }) {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED)
          }
        }
      }
    `
  );
  return (
    <ResourceCardWrapper
      sx={{
        width: width,
        m: margin,
        mb: mb,
      }}>
      <CardLink url={url}>
        <Flex sx={cardStyle}>
          <Box>
            <CardImage
              cardImage={getImage(cardImage)}
              imageUrl={cardImageUrl}
              externalImgAlt={externalImgAlt}
              defaultImg={getImage(defaultImg)}
              cardTitle={title}
            />
            <CardContent
              title={title}
              description={description}
              author={author}
              publishDate={publishDate}
            />
          </Box>
          {topics?.length > 0 ? <Tag topics={topics} /> : null}
        </Flex>
      </CardLink>
    </ResourceCardWrapper>
  );
};

const ResourceCardWrapper = styled(Box)`
  border: 1px solid #ddd;
  box-shadow: 0px 1px 2px #dedede;
  background-color: white;
  overflow: hidden;
  height: 372px;
  :hover {
    transform: scale(1.014);
    transition: 0.3s ease-in-out;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  }
  a {
    text-decoration: none;
  }
  @media (max-width: 600px) {
    width: 70%;
    justify-self: center;
  }
`;

const StyledImg = styled.img`
  height: 172px;
  @media (max-width: 768px) {
    height: 150px;
  }
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const StyledImgWrapper = styled(Box)`
  width: 100%;
  text-align: center;
  height: 172px;
`;

const StyledDescription = styled(Text)`
  width: 100%;
  font-size: 14px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const cardStyle = {
  flexDirection: 'column',
  justifyContent: 'space-between',
  color: '#2d3748',
  height: '100%',
};

const titleStyle = {
  color: 'primary',
  mt: 1,
  mb: 2,
  fontSize: [1, 1, 1, 2],
  height: '65px',
};

export default ResourceCard;
